import React, {
  createContext,
  useCallback,
  useEffect,
  useState,
  useRef
} from "react";
import { Toast } from "@bryntum/schedulerpro";
import {getAllRoles, getMe, loginAsGuest, requestSandboxBoard, userLogout} from "../../network/network.jsx";
import { install401Interceptor, install403And404Interceptor } from "../../network/axiosAgent.jsx";
import {
    SANDBOX_PERMISSIONS_NOT_ALLOWED, SANDBOX_VISITOR_PERMISSIONS_NOT_ALLOWED, SUPER_PERMISSIONS
} from "../../other/constants/PrivilegeTypes.jsx";
import {getRedirectToAgendaIfCantView, getSearchParam, showSessionExpiredAlert, showToast} from "../../other/utils/utils.jsx";
import { useHistory }  from 'react-router-dom'
import {DynamicParamKey, SWIVU_HOME_VIEW} from "../../other/constants/OtherKeys.jsx";
import { addUserIdentifyToLogRocket } from "../../logRocketIntegration.jsx";
import { setAuthToken, clearAuthToken } from "../../network/tokenManager.js";
// Custom context provider that makes login state available to subcomponents.

// AuthContext includes:
// .initialized    true once the authenticated user has been loaded
// .user           the authenticated user
// .login(user)    set the current authenticated user
// .logout()       clear authenticated user and send a logout message to the API server
//

export const UserSpecs = {
  date_joined: null,
  email: null,
  first_name: null,
  /**
   * @type {number[]} groups
   */
  groups: [],
  id: null,
  is_active: null,
  is_staff: null,
  is_superuser: null,
  last_login: null,
  last_name: null,
  user_permissions: [],
  username: null,
  is_guest: null,
  recent_invites: [],
  token: null,
  // mutated values
  isDemoUser: null,
  isAdmin: null,
  /**
   * @description - one of type from PrivilegeTypes
   * @param {number|string} privilegeType
   * @returns {boolean}
   */
  can(_privilegeType) {},
  /**
   * @description - board permissions comes with each board, when switching board the permissions gets set to this field
   * @type {string[]} - one of types from ACCESS_PERMISSIONS id field
   */
  boardPermissions: null,
  /**
   * @param {Object} board - selected board
   */
  setBoardPermissions: (_board) => {}
};

export const AuthSpecs = {
  initialized: false,
  /**
   * @type {UserSpecs|null} user
   */
  user: UserSpecs,
  /**
   * @type {Function} login
   */
  login: () => {},
  /**
   * @type {Function} logout
   */
  logout: () => {},
  /**
   * @type {boolean|"login"|"register"}
   */
  displayAuthForm: false,
  /**
   * @type {(formType: "login"|"register"|null) => {}} setDisplayAuthForm - isShow can be boolean which is default equal to login
   */
  setDisplayAuthForm: (_formType) => {},
  /**
   * @type {boolean}
   * */
  isLoading: false,
  /**
   * @type {(state: boolean) => {}}
   * */
  setLoading: (_state) => {},
  /**
  * @type {{current: {
  *     id:number,
  *     name: string,
  *     grants: {key: number, description: string}[]
  *   }[]}
  * }
  * */
  appRolesRef: {current: []},
  isNewSessionRef: { current: false }
};

const AuthContext = createContext(AuthSpecs);

const overrideUserSetPermissionCheck = (user) => {
  function canCheck(permissionToCheck) {
    if (!permissionToCheck) throw new Error("Permission non defined!");

    // sandbox user (without login) using sandbox board
    if (SANDBOX_VISITOR_PERMISSIONS_NOT_ALLOWED.includes(permissionToCheck)) {
      return !this.isDemoUser;
    }

    // user (after login) using sandbox board
    if (SANDBOX_PERMISSIONS_NOT_ALLOWED.includes(permissionToCheck)) {
      return !this.isSandboxBoard;
    }

    // a superuser is someone who has a superpower
    if (SUPER_PERMISSIONS.includes(permissionToCheck)) {
      return this.isHasFaqEditAccess;
    }

    // user default permissions check
    if (this.boardPermissions) {
      return this.boardPermissions.includes(permissionToCheck);
    }

    return false;
  }

  function setBoardPermissions(board) {
    this.boardPermissions = board.grants;
    this.isHasFaqEditAccess = board.isHasFaqEditAccess;
    this.isSandboxBoard = board.isSandbox;
  }

  Object.defineProperties(user, {
    isDemoUser: { value: user?.is_guest }, // Todo: rename isDemoUser to isSandboxUser
    isHasFaqEditAccess: { value: false, writable: true },
    isSandboxBoard: { value: false, writable: true },
    boardPermissions: { value: null, writable: true }, // the value of this will be decided after current board selection
    setBoardPermissions: { value: setBoardPermissions.bind(user) },
    can: { value: canCheck.bind(user), writable: false }
  });

  return user;
};

const AuthContextProvider = (props) => {
  const [user, setUser] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [displayAuthForm, setDisplayAuthForm] = useState(null);
  const appRolesRef = useRef();
  const isNewSessionRef = useRef(false)

  const history = useHistory()

  // This function checks for invitation link and/or set user authentication state
  const initUserAuth = useCallback(
    async (userData) => {
      // Store auth token if present
      if (userData.token) {
        setAuthToken(userData.token);
      }
      getAllRoles().then((roles) => {
        appRolesRef.current = roles.data;
        setUser(
          overrideUserSetPermissionCheck(userData)
        );
        setInitialized(true);
      })
    },
    []
  );

  const cleanLoadApp = (userData) => {
    // In current behavior where a demo account initiates a login / register request while app already have a demo user
    // the bryntum context needs to be reloaded (similarly as it does after login), so we're first setting user null to let
    // app behave like as it logs out, and then setting user with a minor delay
    setUser(null);
    setInitialized(null);
    setTimeout(() => initUserAuth(userData), 1000);
  };

  const login = (userData) => {
    setLoading(false);
    setDisplayAuthForm(null);
    if (user?.isDemoUser) cleanLoadApp(userData);
    else initUserAuth(userData);
  };

  const logout = useCallback(() => {
    if (window.SwivuAppLoaderInstance) window.SwivuAppLoaderInstance.show();
    // Clear user, which will trigger the display of a login screen.
    // Send a message to the server, which is expected to reply with a response that clears the JWT cookie.
    userLogout()
      .then(() => {
        showToast({
          msg: "You've been logged-out successfully",
          type: "success"
        });
        clearAuthToken();
        setUser(null);
        setDisplayAuthForm({ type: SWIVU_HOME_VIEW.auth });
      })
      .catch(() =>
        showToast({ msg: "Oops, Cannot log you out!", type: "error" })
      )
      .finally(() => {
        if (window.SwivuAppLoaderInstance) window.SwivuAppLoaderInstance.hide();
        // after logout I don't want to display board slug and view-name in url
        history.replace(`/${DynamicParamKey}${window.location.search}`);
      });
  }, [setUser, history]);

  useEffect(() => {
    // Respond to 401 error when user is considered to be logged in by logging out.
    // this fails to intercept 401 response from load/sync.
    install401Interceptor((_error) => {
      if (user) {
        // Clear user, which will trigger the display of a login screen.
        clearAuthToken();
        setUser(null);

        // Give the user some hint to what is going on.
        showSessionExpiredAlert();
        history.push("/")
      }
    });

    install403And404Interceptor((_error) => {
      if(user) {
        if (_error.response.status === 404) {
          Toast.show({
            html: `<h3><p class="feedback-savedmsg">Record not found.</p></h3>`,
            timeout: 10000,
            color: "b-red",
            rootElement: document.body
          });
        } else if (_error.response.status === 403) {
          Toast.show({
            html: `<h3><p class="feedback-savedmsg">Sorry, you can't access this record.</p></h3>`,
            timeout: 10000,
            color: "b-red",
            rootElement: document.body
          });
        }
      }
    });
  }, [setUser, user]);

  useEffect(() => {
    getRedirectToAgendaIfCantView(true, null).then(() => {
      // One time only: contact the API server to fetch the user identified by the current JWT cookie.
      getMe()
        .then(({ data }) => {
          // Store auth token for API requests
          setAuthToken(data.token);
          addUserIdentifyToLogRocket(data);
          return initUserAuth(data)
        })
        .catch(() => {
          if (getSearchParam('guest-board')) {
            setLoading(true)
            setDisplayAuthForm(null);
            requestSandboxBoard(loginAsGuest(), (data) => {
              if (data) {
                isNewSessionRef.current = true;
                login(data);
                setLoading(false);
              } else setDisplayAuthForm({ type: SWIVU_HOME_VIEW.auth })
            })
          } else {
            setDisplayAuthForm({ type: SWIVU_HOME_VIEW.auth })
          }
          setInitialized(true);
        });
    });
  }, [initUserAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        initialized,
        login,
        logout,
        displayAuthForm,
        setDisplayAuthForm,
        isLoading,
        setLoading,
        appRolesRef,
        isNewSessionRef
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

export { AuthContextProvider };
